import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Pencil, Plus, Tablet } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { BarreSites } from '../components/ui/BarreSites';
import { useToast } from '../components/ui/useToast';
import { PassationTest } from '../components/tests/PassationTest';
import { EditeurTest } from '../components/tests/EditeurTest';
import { ModaleCorrection } from '../components/tests/ModaleCorrection';
import { usePerimetre } from '../site/usePerimetre';
import {
  useAttribuerTest,
  useModelesTests,
  usePassationTablette,
  useRemettreTablette,
  useTests,
  type ApprenantTestable,
  type LigneTest,
  type ModeleTest,
} from '../lib/useTestsPositionnement';
import { ErreurApi } from '../lib/api';

type Onglet = 'copies' | 'tests';

function horodatage(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

const CLASSE_ACTION =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition [@media(pointer:coarse)]:min-h-[40px]';

/**
 * Écran « Tests de positionnement » du pôle pédagogique (CDC §6.5).
 *
 * Deux vues : les copies — attribuer, faire passer sur la tablette du centre,
 * corriger, avec le résultat EVA du dossier à côté (TF-441) — et les tests
 * eux-mêmes, listes de questions composées par niveau de formation. Le test applicable dépend de la formation suivie : l'écran ne
 * propose à un apprenant que les tests de son niveau, et l'API refuse les
 * autres. Rien n'est affiché comme corrigé sans correction enregistrée.
 */
export function PedTestsPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useTests(site);
  const modeles = useModelesTests();

  const [onglet, setOnglet] = useState<Onglet>('copies');
  const [attribution, setAttribution] = useState(false);
  const [edition, setEdition] = useState<ModeleTest | 'nouveau' | null>(null);
  const [passation, setPassation] = useState<string | null>(null);
  const [correction, setCorrection] = useState<string | null>(null);

  const compteurs = data?.compteurs;
  const niveaux = useMemo(() => data?.niveaux.map((entree) => entree.niveau) ?? [], [data?.niveaux]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Tests de positionnement"
        sousTitre="Pôle pédagogique · un test par niveau selon la formation"
        actions={
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire onClick={() => setEdition('nouveau')} disabled={niveaux.length === 0}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Nouveau test
            </BoutonSecondaire>
            <BoutonPrincipal onClick={() => setAttribution(true)} disabled={!data}>
              Attribuer un test
            </BoutonPrincipal>
          </div>
        }
      />

      <BarreSites />

      {data && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          {data.correcteur.actif
            ? `Correction assistée par ${data.correcteur.nom} : elle propose une note pour chaque réponse libre, qu’une personne valide avant qu’elle compte.`
            : 'Aucune correction assistée par IA n’est raccordée : les questions à choix sont notées d’office, les réponses libres se notent à la main.'}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur libelle="À passer" valeur={compteurs?.aPasser ?? '—'} detail="tests attribués, pas encore remis" ton="navy" />
        <Compteur libelle="À corriger" valeur={compteurs?.aCorriger ?? '—'} detail="réponses libres à noter" ton="warm" />
        <Compteur libelle="Corrigés" valeur={compteurs?.corriges ?? '—'} detail="score enregistré" ton="mint" />
        <Compteur
          libelle="Score moyen"
          valeur={compteurs?.scoreMoyen === null || compteurs?.scoreMoyen === undefined ? '—' : `${compteurs.scoreMoyen}/100`}
          detail="sur les copies corrigées"
          ton="blue"
        />
      </div>

      {error instanceof ErreurApi && <MessageErreur message={error.message} />}

      <nav className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-card" aria-label="Vues des tests">
        {(
          [
            ['copies', 'Copies'],
            ['tests', 'Listes de questions'],
          ] as const
        ).map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            onClick={() => setOnglet(cle)}
            aria-current={onglet === cle ? 'page' : undefined}
            className={`min-h-[44px] min-w-0 flex-1 rounded-xl px-4 text-sm font-semibold transition sm:flex-none ${
              onglet === cle ? 'bg-codialis-navy text-white' : 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
            }`}
          >
            {libelle}
          </button>
        ))}
      </nav>

      {onglet === 'copies' && (
        <Carte titre="Copies par apprenant">
          {isLoading && <Chargement />}
          {!isLoading && data && data.tests.length === 0 && (
            <div className="px-4 sm:px-6 py-8 text-sm text-codialis-gray">
              Aucun test n’a encore été attribué sur ce périmètre.
            </div>
          )}
          {data && data.tests.length > 0 && (
            <div className="relative overflow-x-auto">
              <table className="w-full min-w-[940px] text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                  <tr>
                    <th className="px-4 py-3 text-left font-bold">Apprenant</th>
                    <th className="px-4 py-3 text-left font-bold">Test</th>
                    <th className="px-4 py-3 text-left font-bold">Score</th>
                    <th className="px-4 py-3 text-left font-bold">EVA</th>
                    <th className="px-4 py-3 text-left font-bold">État</th>
                    <th className="px-4 py-3 text-right font-bold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.tests.map((test) => (
                    <LigneCopie
                      key={test.id}
                      test={test}
                      onPasser={() => setPassation(test.id)}
                      onCorriger={() => setCorrection(test.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      )}

      {onglet === 'tests' && (
        <ListeModeles
          modeles={modeles.data ?? []}
          chargement={modeles.isLoading}
          niveaux={data?.niveaux ?? []}
          onModifier={setEdition}
        />
      )}

      {attribution && data && (
        <ModaleAttribution
          apprenants={data.apprenants}
          modeles={(modeles.data ?? []).filter((modele) => modele.actif)}
          onFermer={() => setAttribution(false)}
        />
      )}
      {edition && (
        <EditeurTest
          modele={edition === 'nouveau' ? null : edition}
          niveaux={niveaux}
          onFermer={() => setEdition(null)}
        />
      )}
      {passation && <PassationTablette testId={passation} onFermer={() => setPassation(null)} />}
      {correction && <ModaleCorrection testId={correction} onFermer={() => setCorrection(null)} />}
    </div>
  );
}

function LigneCopie({
  test,
  onPasser,
  onCorriger,
}: {
  test: LigneTest;
  onPasser: () => void;
  onCorriger: () => void;
}): JSX.Element {
  return (
    <tr className="transition hover:bg-gray-50">
      <td className="px-4 py-3">
        <Link to={`/learners/${test.apprenantId}`} className="font-semibold text-codialis-navy hover:text-codialis-sky">
          {test.apprenant}
        </Link>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-codialis-gray">
          <PastilleSite site={test.siteCode} />
          {test.formation ?? '—'}
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-codialis-navy">
        {test.titre ?? 'Test de positionnement'}
        {test.niveau && <span className="block text-[10px] text-codialis-gray">{test.niveau}</span>}
      </td>
      <td className="px-4 py-3 font-mono text-xs font-semibold text-codialis-navy">
        {test.score === null ? '—' : `${test.score}/${test.scoreSur}`}
      </td>
      <td className="px-4 py-3">
        {test.eva ? (
          <>
            <span className="block font-mono text-xs font-semibold text-codialis-navy">
              {test.eva.score}/{test.eva.scoreSur}
            </span>
            <span className="mt-0.5 block">
              <Pastille texte={test.eva.reperage} ton={test.eva.ton} />
            </span>
          </>
        ) : (
          <span className="text-[11px] text-codialis-gray">Non saisi</span>
        )}
      </td>
      <td className="px-4 py-3">
        <Pastille texte={test.etatLibelle} ton={test.etatTon.replace('badge-', '')} />
        <span className="mt-0.5 block text-[10px] text-codialis-gray">
          {test.etat === 'CORRIGE'
            ? `${horodatage(test.corrigeLe)}${test.corrigePar ? ` · ${test.corrigePar}` : ' · d’office'}`
            : test.etat === 'A_CORRIGER'
              ? `Remis le ${horodatage(test.soumisLe)} · ${test.aNoter} réponse(s) à noter`
              : `Attribué le ${horodatage(test.passeLe)}`}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        {test.etat === 'A_PASSER' ? (
          <button type="button" onClick={onPasser} className={`${CLASSE_ACTION} bg-codialis-navy text-white hover:bg-codialis-deep`}>
            <Tablet className="h-3.5 w-3.5" aria-hidden />
            Faire passer sur tablette
          </button>
        ) : (
          <button
            type="button"
            onClick={onCorriger}
            className={`${CLASSE_ACTION} ${
              test.etat === 'A_CORRIGER'
                ? 'bg-codialis-mint text-codialis-navy hover:brightness-95'
                : 'border border-gray-200 text-codialis-navy hover:bg-white'
            }`}
          >
            <ClipboardList className="h-3.5 w-3.5" aria-hidden />
            {test.etat === 'A_CORRIGER' ? 'Corriger' : 'Voir la copie'}
          </button>
        )}
      </td>
    </tr>
  );
}

function ListeModeles({
  modeles,
  chargement,
  niveaux,
  onModifier,
}: {
  modeles: ModeleTest[];
  chargement: boolean;
  niveaux: { niveau: string; formations: string[] }[];
  onModifier: (modele: ModeleTest) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="min-w-0 lg:col-span-2">
        <Carte titre="Listes de questions">
          {chargement && <Chargement />}
          {!chargement && modeles.length === 0 && (
            <div className="px-4 sm:px-6 py-8 text-sm text-codialis-gray">
              Aucun test n’est encore composé. « Nouveau test » en crée un pour un niveau de formation.
            </div>
          )}
          {modeles.map((modele) => (
            <div key={modele.id} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-codialis-navy">{modele.titre}</div>
                <div className="text-[11px] text-codialis-gray">
                  {modele.niveau} · {modele.questions.length} question(s) · {modele.passages} passage(s)
                </div>
              </div>
              {!modele.actif && <Pastille texte="Retiré" ton="neutral" />}
              <button
                type="button"
                onClick={() => onModifier(modele)}
                className={`${CLASSE_ACTION} border border-gray-200 text-codialis-navy hover:bg-gray-50`}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Modifier
              </button>
            </div>
          ))}
        </Carte>
      </div>
      <Carte titre="Formations par niveau" sansBordure>
        <div className="space-y-3 p-4 sm:p-6">
          {niveaux.length === 0 && (
            <p className="text-sm text-codialis-gray">Aucune formation active n’est enregistrée au catalogue.</p>
          )}
          {niveaux.map((entree) => (
            <div key={entree.niveau} className="rounded-xl bg-gray-50 p-3">
              <div className="text-sm font-semibold text-codialis-navy">{entree.niveau}</div>
              <div className="mt-0.5 text-[11px] text-codialis-gray">{entree.formations.join(' · ')}</div>
              <div className="mt-1 text-[11px] text-codialis-gray">
                {modeles.filter((modele) => modele.actif && modele.niveau === entree.niveau).length} test(s) proposé(s)
              </div>
            </div>
          ))}
        </div>
      </Carte>
    </div>
  );
}

/**
 * Attribution d'un test : seuls les tests du niveau de la formation suivie
 * sont proposés, l'API refusant les autres.
 */
function ModaleAttribution({
  apprenants,
  modeles,
  onFermer,
}: {
  apprenants: ApprenantTestable[];
  modeles: ModeleTest[];
  onFermer: () => void;
}): JSX.Element {
  const notifier = useToast();
  const attribuer = useAttribuerTest();
  const [apprenantId, setApprenantId] = useState('');
  const [modeleId, setModeleId] = useState('');

  const apprenant = apprenants.find((entree) => entree.id === apprenantId) ?? null;
  const applicables = apprenant ? modeles.filter((modele) => modele.niveau === apprenant.niveau) : [];

  return (
    <Modale titre="Attribuer un test" sousTitre="Le test applicable dépend de la formation suivie" ouverte onFermer={onFermer}>
      <div className="space-y-4 p-4 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Apprenant</span>
          <select
            value={apprenantId}
            onChange={(e) => {
              setApprenantId(e.target.value);
              setModeleId('');
            }}
            className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy"
          >
            <option value="">Choisir un apprenant…</option>
            {apprenants.map((entree) => (
              <option key={entree.id} value={entree.id}>
                {entree.nom} · {entree.siteCode} · {entree.formation}
              </option>
            ))}
          </select>
        </label>

        {apprenant && (
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Test · {apprenant.niveau}
            </span>
            {applicables.length === 0 ? (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Aucun test pour le niveau « {apprenant.niveau} » : composez-en un dans « Listes de questions ».
              </p>
            ) : (
              <select
                value={modeleId}
                onChange={(e) => setModeleId(e.target.value)}
                className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy"
              >
                <option value="">Choisir un test…</option>
                {applicables.map((modele) => (
                  <option key={modele.id} value={modele.id}>
                    {modele.titre} · {modele.questions.length} question(s)
                  </option>
                ))}
              </select>
            )}
          </label>
        )}

        {attribuer.error instanceof ErreurApi && <MessageErreur message={attribuer.error.message} />}

        <div className="flex flex-wrap gap-2">
          <BoutonPrincipal
            onClick={() =>
              attribuer.mutate(
                { apprenantId, modeleId },
                {
                  onSuccess: ({ message }) => {
                    notifier(message);
                    onFermer();
                  },
                },
              )
            }
            disabled={!apprenantId || !modeleId || attribuer.isPending}
          >
            {attribuer.isPending ? 'Attribution…' : 'Attribuer'}
          </BoutonPrincipal>
          <BoutonSecondaire onClick={onFermer} disabled={attribuer.isPending}>
            Annuler
          </BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}

/**
 * Passation sur la tablette du centre : on tend l'appareil à l'apprenant. La
 * fenêtre occupe l'écran ; à la remise, elle dit seulement que le test est
 * remis — le score n'est pas à lire par-dessus l'épaule de l'apprenant.
 */
function PassationTablette({ testId, onFermer }: { testId: string; onFermer: () => void }): JSX.Element {
  const notifier = useToast();
  const { data, isLoading, error } = usePassationTablette(testId);
  const remettre = useRemettreTablette();

  return (
    <Modale
      titre={data?.titre ?? 'Test de positionnement'}
      sousTitre={data ? `Passation sur tablette · ${data.apprenant}` : undefined}
      ouverte
      onFermer={onFermer}
      largeur="max-w-4xl"
    >
      <div className="p-4 sm:p-6">
        {isLoading && <Chargement libelle="Chargement du test…" />}
        {error instanceof ErreurApi && <MessageErreur message={error.message} />}
        {data && data.soumisLe && (
          <p className="text-sm text-codialis-gray">Ce test a déjà été remis le {horodatage(data.soumisLe)}.</p>
        )}
        {data && !data.soumisLe && (
          <PassationTest
            passation={data}
            enCours={remettre.isPending}
            erreur={remettre.error instanceof ErreurApi ? remettre.error.message : null}
            onAnnuler={onFermer}
            onRemettre={(reponses) =>
              remettre.mutate(
                { testId, reponses },
                {
                  onSuccess: () => {
                    notifier('Test remis. Merci !');
                    onFermer();
                  },
                },
              )
            }
          />
        )}
      </div>
    </Modale>
  );
}
